// API服务测试工具
import { azureTtsService } from '../services/azureTtsService.js'
import { openaiChatService } from '../services/openaiChatService.js'
import { API_CONFIG, validateApiConfig, getConfigSummary, checkServerHealth } from '../services/apiConfig.js'

export class ApiTestSuite {
  constructor() {
    this.testResults = []
  }

  /**
   * 运行完整的API测试套件
   */
  async runFullTestSuite() {
    console.log('🧪 开始API服务测试套件...')
    
    this.testResults = []
    
    // 测试API配置
    await this.testApiConfig()
    
    // 测试Azure TTS
    await this.testAzureTTS()
    
    // 测试OpenAI Chat
    await this.testOpenAIChat()
    
    // 输出测试报告
    this.printTestReport()
    
    return this.testResults
  }

  /**
   * 测试API配置
   */
  async testApiConfig() {
    try {
      console.log('📋 测试API配置...')
      
      const isValid = validateApiConfig()
      const configSummary = getConfigSummary()
      
      this.addTestResult('API配置验证', true, {
        summary: configSummary,
        isValid
      })
      
      console.log('✅ API配置测试通过')
      
    } catch (error) {
      this.addTestResult('API配置验证', false, { error: error.message })
      console.error('❌ API配置测试失败:', error)
    }
  }

  /**
   * 测试Azure TTS服务
   */
  async testAzureTTS() {
    try {
      console.log('🎤 测试Azure TTS服务...')
      
      // 验证配置
      azureTtsService.validateConfiguration()
      
      // 测试语音列表获取
      const voices = azureTtsService.getAvailableVoices()

      this.addTestResult('Azure TTS配置', true, {
        endpoint: API_CONFIG.api.tts,
        voicesCount: Object.keys(voices).length
      })

      // 注意：实际的TTS API调用需要真实的音频播放测试
      console.log('✅ Azure TTS配置测试通过（未测试实际API调用）')
      
    } catch (error) {
      this.addTestResult('Azure TTS配置', false, { error: error.message })
      console.error('❌ Azure TTS测试失败:', error)
    }
  }

  /**
   * 测试OpenAI Chat服务
   */
  async testOpenAIChat() {
    try {
      console.log('🤖 测试OpenAI Chat服务...')
      
      // 验证配置
      openaiChatService.validateConfiguration()
      
      // 测试对话历史管理
      openaiChatService.addToHistory('user', '测试消息')
      const stats = openaiChatService.getConversationStats()
      
      if (stats.totalMessages !== 1) {
        throw new Error('对话历史管理异常')
      }
      
      // 清理测试数据
      openaiChatService.clearHistory()
      
      this.addTestResult('OpenAI Chat配置', true, {
        endpoint: API_CONFIG.api.chat,
        historyManagement: true
      })
      
      // 注意：实际的OpenAI API调用需要网络连接测试
      console.log('✅ OpenAI Chat配置测试通过（未测试实际API调用）')
      
    } catch (error) {
      this.addTestResult('OpenAI Chat配置', false, { error: error.message })
      console.error('❌ OpenAI Chat测试失败:', error)
    }
  }

  /**
   * 测试服务端代理是否可用、Secrets 是否已配置（不调用 OpenAI / Azure，不消耗额度）
   */
  async testApiConnectivity() {
    console.log('🌐 测试API连通性...')

    const results = { azure: false, openai: false, errors: [] }

    try {
      const health = await checkServerHealth()
      results.openai = Boolean(health.chat)
      results.azure = Boolean(health.tts)
      if (!results.openai) results.errors.push('服务端未配置 OPENAI_API_KEY')
      if (!results.azure) results.errors.push('服务端未配置 AZURE_SPEECH_KEY / AZURE_SPEECH_REGION')
    } catch (error) {
      results.errors.push(`/api/health: ${error.message}`)
      console.warn('⚠️ /api/health 不可用:', error.message)
    }

    this.addTestResult('API连通性', results.azure && results.openai, results)

    return results
  }

  /**
   * 添加测试结果
   */
  addTestResult(testName, success, details = {}) {
    this.testResults.push({
      testName,
      success,
      details,
      timestamp: new Date()
    })
  }

  /**
   * 打印测试报告
   */
  printTestReport() {
    console.log('\n📊 API测试报告')
    console.log('='.repeat(50))
    
    const successful = this.testResults.filter(r => r.success).length
    const total = this.testResults.length
    
    console.log(`总测试: ${total}, 成功: ${successful}, 失败: ${total - successful}`)
    console.log('='.repeat(50))
    
    this.testResults.forEach((result, index) => {
      const status = result.success ? '✅' : '❌'
      console.log(`${index + 1}. ${status} ${result.testName}`)
      
      if (!result.success && result.details.error) {
        console.log(`   错误: ${result.details.error}`)
      }
      
      if (result.success && Object.keys(result.details).length > 0) {
        console.log(`   详情:`, result.details)
      }
    })
    
    console.log('='.repeat(50))
  }
}

// 导出测试实例
export const apiTestSuite = new ApiTestSuite()

// 在开发环境中自动运行基础测试
if (process.env.NODE_ENV === 'development') {
  // 延迟执行，避免在模块加载时立即运行
  setTimeout(() => {
    if (API_CONFIG.debug.enableLogs) {
      console.log('🔧 开发模式：运行API基础配置检查...')
      apiTestSuite.testApiConfig()
      apiTestSuite.testAzureTTS()
      apiTestSuite.testOpenAIChat()
    }
  }, 1000)
}